import {EHttpStatusCode, IEndPoint, IO, IResponse, UrlQs} from "lib/commons/io/io";
import {JLogMsg} from "lib/core/universe";
import {LANG} from "lib/commons/lang";

/**
 * Accès au service CID d'un dépot Scenari.
 */
export class DepotCidSrv {

	/** Messages connus prioritaires localisés. */
	static errorMsgMain = new Map<string, string>();

	/** Messages connus secondaires. */
	static errorMsgSecond = new Map<string, string>();

	constructor(public readonly config: OCidSrvConfig) {}

	/** Création d'une session CID asynchrone. */
	newSession(): DepotCidSession {return new DepotCidSession(this)}

	/**
	 * Exécution d'un process CID en 1 seule requête synchrone (pas de session).
	 * Le server attend la fin du process CID avant de retourner la réponse.
	 */
	syncSend<R>(request: ODepotCidRequest<any, R>): Promise<JDepotCidResult & R> {
		(request as ODepotCidRequestInternal<any, R>).synch = true;
		request.createMetas = true;
		if (!request.body) request.scContent = 'none';
		return sendCidRequest<R>(this.config.cidUrl, request, null);
	}

	/** A utiliser si DepotCidSession non dispo, mais qu'on possède un sessionId (interactions CID). */
	sendRequestToSession<R>(sessionId: string, request: ODepotCidRequest<any, R>): Promise<JDepotCidResult & R> {
		return sendCidRequest<R>(this.config.cidUrl, request, sessionId);
	}

	/** Recherche d'un message connu localisé. */
	extractCidLocalizedErrorMsg(err: any): string {
		while (err) {
			if (Array.isArray((err as JDepotCidResult).scCidSessDetails)) {
				err = (err as JDepotCidResult).scCidSessDetails.find((entry) => entry.type === "error");
			} else if (typeof (err as JDepotCidSessDetail).msg) {
				const m = this.findLocalizedErrorMsg(err as JDepotCidSessDetail, DepotCidSrv.errorMsgMain) || this.findLocalizedErrorMsg(err as JDepotCidSessDetail, DepotCidSrv.errorMsgSecond);
				if (m) return m;
				break;
			}
		}
		return "Une erreur inconnue est survenue";
	}

	findLocalizedErrorMsg(cidDetail: JDepotCidSessDetail, map: Map<string, string>): string {
		const m = map.get(cidDetail.msg);
		if (m) return LANG.formatStr(m, cidDetail.vars);
		let logMsg = cidDetail.details;
		while (logMsg) {
			const m = map.get(logMsg.desc);
			if (m) return LANG.formatStr(m, logMsg.vars);
			logMsg = logMsg.parent;
		}
		return null;
	}
}

function fillMainMsg(map: Map<string, string>) {
	map.set("Concurrent update while editing '%s'", "Cette ressource '%s' a été modifiée entre temps, vos modifications n'ont pu être prises en compte.");
	map.set("Concurrent update failed for '%s' (previous version not found)", "Cette ressource '%s' a probablement été supprimée entre temps, vos modifications n'ont pu être prises en compte.");
	map.set("Cid task started denied due to server closing", "L'enregistrement de cette ressource a échoué car le serveur est momentanément indisponible.");
	map.set("Cid task started denied due to concurrent processes", "L'enregistrement de cette ressource a échoué car d'autres modifications sont en cours de traitement");
}

fillMainMsg(DepotCidSrv.errorMsgMain);

// function fillSecondaryMsg(map: Map<string, string>) {
// }
// fillSecondaryMsg(DepotCidSrv.errorMsgSecond);


/** Configuration d'un serveur CID d'un dépot Scenari. */
export interface OCidSrvConfig {

	/** URL CID dans le contexte authentifié. */
	cidUrl?: IEndPoint;

	/**
	 * URL CID dans le contexte non authentifié pour le traitement d'une session ouverte.
	 * (La clé de la session en cours constitue la clé de sécurité)
	 */
	cidInSessionUrl?: IEndPoint;
}

/** Configuration par défaut du service cid à partir de la config de l'univers. */
export function configDepotCidSrv(authenticatedExecFrameUrl: IEndPoint, publicExecFrameUrl?: IEndPoint, config?: OCidSrvConfig): OCidSrvConfig {
	if (!config) config = {} as OCidSrvConfig;
	if (!config.cidUrl) config.cidUrl = authenticatedExecFrameUrl.resolve("u/cid");
	if (publicExecFrameUrl && !config.cidInSessionUrl) config.cidInSessionUrl = publicExecFrameUrl.resolve("u/cid");
	if (!config.cidInSessionUrl) config.cidInSessionUrl = config.cidUrl; //si pas d'accès public, les requêtes de session d'executent dans le contexte authentifié.
	return config;
}

/**
 * Définit une requête CID
 * P (Params) représente les paramètres CID de la requête.
 * R (Response) représente les propriétés de la réponse JSON.
 */
export interface ODepotCidRequest<P, R> {

	/** Metas CID à envoyer. */
	cidMetas: P;

	/** Contenu à envoyer. */
	body?: ODepotCidBody;

	/**
	 * Directive pour indiquer que toutes les metas ont été transimses et exiger la création de la ressource.
	 * Le contenu/body doit avoir aussi été envoyé ou la directive scContent='none' doit aussi être renseignée.
	 */
	createMetas?: boolean;

	/** Directive pour indiquer l'absence de contenu/body et permet de finaliser la session CID sans attendre de contenu dans un body. */
	scContent?: 'none';

	/** Notifie l'avancement dans la phase d'upload du body. */
	progress?: (done: number, total: number | undefined) => void

	/**
	 * Liste les propriétés à retourner dans le json résultant de la requêt CID.
	 * Toutes les props de OCidResponse ainsi que celles propres à chaque nature de task CID
	 * peuvent être listées (exemple : props de OPersistMetas).
	 * Sous forme string : les paramètres doivent être séparés par des '*'.
	 */
	returnProps?: string | Array<keyof (JDepotCidResult & R)>;
}

export interface ODepotCidRequestInternal<O, R> extends ODepotCidRequest<O, R> {

	/**
	 * Directive définissant les StatusCode http de réponse attendus.
	 *
	 * <p>Valeurs possibles :
	 * <ul>
	 * <li>"200-403-500" [Valeur par défaut] status-code 200 (ou 204 si pas de body dans la resp), 403 (pas autorisé) ou 500 en cas d'echec.
	 * <li>"200" retourne toujours 200 (voir 204 si pas de body): les erreurs sont retournés dans le body de la réponse.
	 * <li>"200-202-403-500" en plus "200-403-500" peut retourner 202 pour indiquer que le traitement est en cours.
	 * <li>"200-202" en plus "200" peut retourner 202 pour indiquer que le traitement est en cours.
	 * <li>Toute autre valeur qui devra etre traitée par la task en cours (les 4 premières valeurs sont gérées directement par le serverCid).
	 * </ul>
	 */
	scResp?: string;

	/** Ajoute un cookie "scCidSessId" contenant l'id de la session. */
	addSessionIdAsCookie?: boolean;

	/** Force la requête à attendre que la session CID soit achevée avant de retourner sa réponse. */
	synch?: boolean;

	/** Mode d'envoi des metas CID. */
	cidMetasLocation?: 'post' | 'queryString' | 'header' //='post' par défaut
}

export interface JDepotCidResult {
	scCidSessId?: string;
	scCidSessStatus?: EDepotCidStatus;
	scCidSessDetails?: JDepotCidSessDetail[];
}

export interface JDepotCidSessDetail {
	type: 'stepDone' | 'stepPending' | 'stepForthcoming' | 'info' | 'warning' | 'error';
	msg: string;
	vars: string[];
	progression: number;
	progressionTotal: number;
	details: JLogMsg
}

export type ODepotCidBody = Blob | Int8Array | Int16Array | Int32Array | Uint8Array | Uint16Array | Uint32Array | Uint8ClampedArray | Float32Array | Float64Array | DataView | ArrayBuffer | FormData | string | null;

export type EDepotCidStatus =

/** Statut initial, traitement en cours */
	'working'

	/** La tâche est en attente d'un retour utilisateur. */
	| 'waitingUserInput'

	/** La tâche est en attente du commit final. */
	| 'waitingForCommit'

	/** Commit de la tâche en cours. */
	| 'commiting'

	/** Rollback de la tâche en cours. */
	| 'rollbacking'

	/** La tâche est achevée, commitée. */
	| 'commited'

	/** La tâche est en echec ou a été rollbackée. */
	| 'rollbacked'

	/** La tâche est en echec (après rollback ou revert). */
	| 'failed';


export class DepotCidSession {

	/** Id de la session (obtenue une fois démarrée). */
	sessionId: string;

	constructor(public cidServer: DepotCidSrv) {}

	/** Optionnel : démarre la session, sans aucune action. */
	startSession(): Promise<JDepotCidResult> {
		if (this.sessionId) throw Error("Cid session  already started.");
		return sendCidRequest(this.cidServer.config.cidUrl, {cidMetas: {}, returnProps: 'scCidSessId'}, null).then((r: JDepotCidResult) => {
			this.sessionId = r.scCidSessId;
			return r;
		});
	}

	/**
	 * Envoie une requete à la session. Si c'est la 1ère requete et startSession() n'a pas été appelée, la session est automatiquement créée.
	 * La promesse retourne le 1er retour du serveur après l'éventuel upload et n'indique pas que la session est terminée.
	 * @see JDepotCidResult.scCidSessStatus pour l'avancement de la session à ce 1er retour.
	 * @see waitForEndSession() pour attendre la fin de la session.
	 */
	send<R>(request: ODepotCidRequest<any, R>): Promise<JDepotCidResult & R> {
		if (this.sessionId) return sendCidRequest<R>(this.cidServer.config.cidUrl, request, this.sessionId);
		//1er appel, on ajoute "scCidSessId" aux returnProps
		request.returnProps = appendSessId<R>(request.returnProps);
		return sendCidRequest<R>(this.cidServer.config.cidInSessionUrl, request, null).then((r: JDepotCidResult & R) => {
			this.sessionId = r.scCidSessId;
			return r;
		});
	}

	/**
	 * Interroge le server jusqu'à ce que la session soit close, ie la tâche CID soit achevée.
	 *
	 * @param intervalInMs interval de temps entre 2 appels au serveur.
	 * @param intermediateCb callback appelé à chaque appel du serveur qui retroune un résultat non achevé de la session.
	 */
	waitForEndSession<R>(returnProps?: string | Array<keyof (JDepotCidResult & R)>, intervalInMs: number = 800, intermediateCb?: (resp: JDepotCidResult & R) => void): Promise<JDepotCidResult & R> {
		if (!this.sessionId) throw Error("Cid session never started.");
		let path = "?cdaction=RequestSession&scCidSessId=" + this.sessionId + "&scResp=200-202";
		//TODO ajouter un param sur le serveur qui ne produit la réponse JSON complète que quand la session est achevée.
		if (returnProps) path = path + "&returnProps=" + (Array.isArray(returnProps) ? returnProps.join('*') : returnProps);
		let interval = intervalInMs / 4;
		const waitEndSession = (resolve: (v: PromiseLike<JDepotCidResult & R>) => void, reject: (reason?: any) => void) => {
			setTimeout(() => {
				try {
					this.cidServer.config.cidInSessionUrl.fetch(path, 'none').then((r: IResponse) => {
						if (r.status === EHttpStatusCode.accepted) {
							//on attend
							if (intermediateCb) r.json().then(intermediateCb);
							if (interval < intervalInMs) interval *= 2;
							waitEndSession(resolve, reject);
						} else {
							resolve(returnProps ? r.json() as Promise<JDepotCidResult & R> : null);
						}
					}, reject /* en principe inutile! */);
				} catch (e) {
					reject(e);
				}
			}, interval);
		};
		return new Promise(waitEndSession);
	}
}

function appendSessId<R>(props: string | Array<keyof (JDepotCidResult & R)>): string | Array<keyof (JDepotCidResult & R)> {
	if (!props) return 'scCidSessId';
	if (Array.isArray(props)) {
		if (props.indexOf('scCidSessId') < 0) props.push('scCidSessId');
	} else {
		if (props.indexOf('scCidSessId') < 0) props += "*scCidSessId";
	}
	return props;
}


function sendCidRequest<R>(endPoint: IEndPoint, request: ODepotCidRequestInternal<any, R>, scCidSessId: string, interactionLang?: string): Promise<JDepotCidResult & R> {
	const initReq = {method: 'POST'} as RequestInit;
	const qs = new UrlQs();
	if (scCidSessId) {
		qs.append('cdaction', 'RequestSession');
		qs.append('scCidSessId', scCidSessId);
	} else {
		qs.append('cdaction', 'StartSession');
	}

	let cidInteractionLang = interactionLang;
	if (!cidInteractionLang) {
		const reqInit = IO.addLang();
		if (reqInit?.headers) cidInteractionLang = (reqInit.headers as any)[IO.REQUEST_LANG_HEADER]
	}
	if (cidInteractionLang)
		qs.append('interactionLang', cidInteractionLang);
	if (request.synch) qs.append('synch', 'true');
	if (request.createMetas) qs.append('createMetas', 'true');
	if (request.scContent) qs.append('scContent', request.scContent);
	if (request.returnProps) qs.append('returnProps', Array.isArray(request.returnProps) ? request.returnProps.join('*') : request.returnProps);
	qs.append("scResp", request.scResp || '200-403'); //Pas d'erreur 500 pour obtenir le json JDepotCidResult

	if (request.cidMetasLocation === 'queryString') {
		for (const key in request.cidMetas) {
			const v = request.cidMetas[key];
			if (v != null) qs.append(key, typeof v === "object" ? JSON.stringify(v) : v);
		}
		if (request.body) {
			initReq.method = 'PUT';
			initReq.headers = {'Content-Type': "application/binary"};
			initReq.body = request.body;
		}
	} else if (request.cidMetasLocation === 'header') {
		initReq.headers = {};
		for (const key in request.cidMetas) {
			const v = request.cidMetas[key];
			if (v != null) initReq.headers[key] = typeof v === "object" ? JSON.stringify(v) : v;
		}
		initReq.body = request.body;
		if (request.body) {
			initReq.method = 'PUT';
			initReq.body = request.body;
		}
	} else {
		initReq.method = 'POST';
		const formData = new FormData();
		for (const key in request.cidMetas) {
			const v = request.cidMetas[key];
			if (v != null) formData.append(key, typeof v === "object" ? JSON.stringify(v) : v);
		}
		if (request.body) {
			if (request.body instanceof ArrayBuffer) request.body = new Blob([request.body]);
			formData.set('cidContent', request.body as any);
		}
		initReq.body = formData;
	}
	if (request.progress) {
		//En attendant une API plus propre https://github.com/whatwg/fetch/issues/607
		return new Promise<JDepotCidResult & R>((resolved, rejected) => {
			const xhr = new XMLHttpRequest();
			xhr.upload.addEventListener("progress", (ev) => {request.progress(ev.loaded, ev.lengthComputable ? ev.total : undefined)});
			xhr.onload = (ev) => {resolved(JSON.parse(xhr.responseText))};
			xhr.onerror = rejected;
			xhr.withCredentials = true;
			if (initReq.headers) for (let h in initReq.headers) {
				xhr.setRequestHeader(h, (initReq.headers as any)[h]); //A améliorer : initReq.headers plus riche...
			}
			xhr.open(initReq.method, endPoint.url + '?' + qs.toString());
			xhr.send(initReq.body);
		});
	}
	return endPoint.fetchJson('?' + qs.toString(), initReq);
}


// export class CidRequestBuilder implements OCidRequest<any> {
// 	cidMetas = {} as any;
// 	body: Blob | BufferSource | USVString;
// 	returnProps: string | string[];
// 	scContent: 'none'
//
// 	setPath(path: string): this {
// 		this.cidMetas.path = path;
// 		return this;
// 	}
//
// 	setReturnProps(props: string | string[]): this {
// 		this.returnProps = props;
// 		return this;
// 	}
//
// 	setProcessing(processing: string): this {
// 		this.cidMetas.processing = processing;
// 		return this;
// 	}
//
// 	setRemove(): this {
// 		this.cidMetas.processing = 'remove';
// 		return this;
// 	}
//
// 	setTrash(): this {
// 		this.cidMetas.trash = true; // A VERIFIER
// 		return this;
// 	}
//
// 	setUntrash(): this {
// 		this.cidMetas.trash = false; // A VERIFIER
// 		return this;
// 	}
//
// 	setBody(body: Blob | BufferSource | USVString): this {
// 		this.body = body;
// 		return this;
// 	}
//
// 	setNoContent(): this {
// 		this.scContent = 'none';
// 		return this;
// 	}
// }